import React from 'react';
import { Music, User, Plus, Play, Pause, Check, Loader2 } from 'lucide-react';
import { AvailableRoutineCardProps } from '../../types/competition';
import { Routine } from '../../types/registration';

interface ExtendedAvailableRoutineCardProps extends AvailableRoutineCardProps {
  onToggleSelection: (routine: Routine) => void;
  onToggleAudio: (routine: Routine) => void;
  isAddingRoutine?: boolean;
}

/**
 * Card component for an available routine that can be selected
 * Updated to work with immutable clone-on-register approach
 */
const AvailableRoutineCard: React.FC<ExtendedAvailableRoutineCardProps> = ({ 
  routine, 
  onToggleSelection, 
  onToggleAudio, 
  currentlyPlaying, 
  isDarkMode,
  isSelected = false,  // Indicates if this routine is already selected
  isAddingRoutine = false  // New prop to indicate loading state
}) => {
  // Format age information
  const formatAgeInfo = () => {
    if (routine.averageAge) {
      return `Avg. Age: ${routine.averageAge}`;
    }
    return 'Avg. Age: N/A';
  };

  const isPlaying = currentlyPlaying === routine.routineId;
  const hasSong = Boolean(routine.songFileUrl);
  const dancerCount = Array.isArray(routine.dancers) ? routine.dancers.length : 0;
  
  return (
    <div
      className={`border rounded-lg p-3 flex items-start gap-3 transition-all ${
        isDarkMode
          ? isSelected
            ? 'border-transparent bg-blue-900/20 shadow-glow-blue'
            : 'border-gray-700 hover:border-blue-500/40 hover:bg-blue-900/10'
          : isSelected
            ? 'border-transparent bg-blue-50 shadow-md'
            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
      }`}
    >
      {/* Left Icon */}
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isDarkMode
            ? isSelected
              ? 'bg-blue-800/30 text-blue-400'
              : 'bg-gray-800 text-gray-400'
            : isSelected
              ? 'bg-blue-100 text-blue-600'
              : 'bg-gray-100 text-gray-500'
        }`}
      >
        <Music className="w-4 h-4" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start mb-1">
          <h3
            className={`font-medium text-sm truncate ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}
          >
            {routine.title || routine.name || 'Untitled Routine'}
          </h3>
          
          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
            {/* Play Audio Button */}
            {hasSong && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleAudio(routine);
                }}
                className={`p-1 rounded-full transition-all ${
                  isPlaying
                    ? isDarkMode
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-blue-100 text-blue-600'
                    : isDarkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
                aria-label={isPlaying ? 'Pause song' : 'Play song'}
                title={isPlaying ? 'Pause song' : 'Play song'}
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
              </button>
            )}

            {/* Add/Remove Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Only allow toggle if not currently adding
                if (!isAddingRoutine) {
                  onToggleSelection(routine);
                }
              }}
              disabled={isAddingRoutine} // Disable when in loading state
              className={`p-1 rounded-full transition-all ${
                isAddingRoutine
                  ? isDarkMode 
                    ? 'bg-gray-800 text-gray-500 cursor-wait' 
                    : 'bg-gray-200 text-gray-500 cursor-wait'
                  : isSelected
                    ? isDarkMode
                      ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                      : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                    : isDarkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              aria-label={
                isAddingRoutine 
                  ? 'Adding routine...' 
                  : isSelected 
                    ? 'Remove routine' 
                    : 'Add routine'
              }
              title={
                isAddingRoutine 
                  ? 'Adding routine...' 
                  : isSelected 
                    ? 'Remove routine' 
                    : 'Add routine'
              }
            >
              {isAddingRoutine ? (
                <Loader2 size={14} className="animate-spin" />
              ) : isSelected ? (
                <Check size={14} />
              ) : (
                <Plus size={14} />
              )}
            </button>
          </div>
        </div>

        {/* Details */}
        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 gap-x-2 flex-wrap">
          <span className="flex items-center">
            <User size={10} className="mr-1" />
            {dancerCount} {dancerCount === 1 ? 'dancer' : 'dancers'}
          </span>
          <span>Type: {routine.typeLabel || routine.type || 'Unknown'}</span>
          <span>{formatAgeInfo()}</span>
        </div>
      </div>
    </div>
  );
};

export default AvailableRoutineCard; 